import { h, Component } from 'preact';
import { createPortal } from 'preact/compat';

export const eventTypeColors = {
    program: '#4CAF50',      // Green
    commercial: '#FF9800',   // Orange
    promo: '#9C27B0',        // Purple
    ident: '#2196F3',        // Blue
    news: '#F44336',         // Red
    live: '#E91E63',         // Pink
    break: '#795548',        // Brown
    filler: '#9E9E9E',       // Grey
};

const sourceTypeIcons = {
    live: 'fa-solid fa-broadcast-tower',
    file: 'fa-solid fa-file-video',
    feed: 'fa-solid fa-rss',
    manual: 'fa-solid fa-hand',
    placeholder: 'fa-regular fa-square',
};

class EventBlock extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isDragging: false,
            isResizing: false,
            showTooltip: false,
            tooltipX: 0,
            tooltipY: 0,
            showOnRight: true,
        };
    }

    handleClick = e => {
        e.stopPropagation();
        const { event, onClick } = this.props;
        if (onClick) {
            onClick(event);
        }
    };

    handleDragStart = e => {
        const { event, canEdit } = this.props;
        if (!canEdit) {
            e.preventDefault();
            return;
        }

        e.stopPropagation();
        this.setState({ isDragging: true });
        e.dataTransfer.setData('application/json', JSON.stringify(event));
        e.dataTransfer.effectAllowed = 'move';
    };

    handleDragEnd = () => {
        this.setState({ isDragging: false });
    };

    handleResizeStart = e => {
        const { canEdit, hourHeight = 80 } = this.props;
        if (!canEdit) return;

        e.stopPropagation();
        e.preventDefault();
        this.setState({ isResizing: true });

        const startY = e.clientY;
        const startHeight = this.blockRef?.offsetHeight || 0;
        const pixelsPerMinute = hourHeight / 60;
        const minHeight = pixelsPerMinute; // Minimum 1 minute

        const handleMouseMove = moveEvent => {
            const deltaY = moveEvent.clientY - startY;
            const rawHeight = startHeight + deltaY;
            // Snap to minute intervals
            const minutes = Math.max(1, Math.round(rawHeight / pixelsPerMinute));
            const snappedHeight = minutes * pixelsPerMinute;
            if (this.blockRef) {
                this.blockRef.style.height = `${snappedHeight}px`;
            }
        };

        const handleMouseUp = upEvent => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            this.setState({ isResizing: false });

            // Calculate new end time based on final height (snapped to minutes)
            const { event, onResized } = this.props;
            const newHeight = this.blockRef?.offsetHeight || startHeight;
            const durationMinutes = Math.max(1, Math.round(newHeight / pixelsPerMinute));
            const durationMs = durationMinutes * 60 * 1000;
            const newEnd = new Date(new Date(event.scheduledStart).getTime() + durationMs);

            if (onResized) {
                onResized(event, newEnd.toISOString());
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    formatTime(dateStr) {
        const d = new Date(dateStr);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }

    formatDuration(seconds) {
        if (!seconds) return '';
        const mins = Math.floor(seconds / 60);
        if (mins < 60) return `${mins}m`;
        const hours = Math.floor(mins / 60);
        const remMins = mins % 60;
        return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
    }

    formatDurationFromDates(start, end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const durationMs = endDate - startDate;
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) {
            return minutes > 0 ? `${hours}t ${minutes}m` : `${hours}t`;
        }
        return `${minutes}m`;
    }

    formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' });
    }

    handleMouseEnter = e => {
        const { isCompact } = this.props;
        if (!isCompact) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Determine if tooltip should show on right or left
        const showOnRight = rect.left < windowWidth / 2;

        // Calculate position
        let tooltipX, tooltipY;
        if (showOnRight) {
            tooltipX = rect.right + 10;
        } else {
            tooltipX = rect.left - 310; // 300px width + 10px margin
        }

        // Vertical position - try to align with the block, but keep in viewport
        tooltipY = Math.max(10, Math.min(rect.top, windowHeight - 300));

        this.hoverTimeout = setTimeout(() => {
            this.setState({
                showTooltip: true,
                tooltipX,
                tooltipY,
                showOnRight,
            });
        }, 300);
    };

    handleMouseLeave = () => {
        if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
        }
        this.setState({ showTooltip: false });
    };

    renderTooltip() {
        const { event, darkmode, isTruncated, isContinuation, totalDays, dayNumber, channelColor } = this.props;
        const { tooltipX, tooltipY } = this.state;

        const bgColor = event.backgroundColor || event.color || channelColor || eventTypeColors[event.eventType] || '#6c757d';

        return (
            <div
                style={{
                    position: 'fixed',
                    top: `${tooltipY}px`,
                    left: `${tooltipX}px`,
                    backgroundColor: darkmode ? '#1f2937' : '#ffffff',
                    color: darkmode ? '#f3f4f6' : '#1f2937',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    border: darkmode ? '1px solid #374151' : '1px solid #e5e7eb',
                    zIndex: 99999,
                    width: '300px',
                    fontSize: '0.85rem',
                    pointerEvents: 'none',
                }}
            >
                {/* Color bar at top */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        backgroundColor: bgColor,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                    }}
                />

                <div class='fw-bold mb-2' style={{ fontSize: '1rem', marginTop: '4px' }}>
                    {event.title || 'Untitled'}
                    {event.timingType === 'hard' && (
                        <i class='fa-solid fa-lock ms-2' style={{ fontSize: '0.75rem', opacity: 0.7 }} title='Hard timing' />
                    )}
                </div>
                {event.subtitle && (
                    <div class='mb-2 text-muted' style={{ fontSize: '0.85rem' }}>
                        {event.subtitle}
                    </div>
                )}
                <div class='mb-1'>
                    <i class='fa-regular fa-calendar me-2' style={{ width: '14px' }} />
                    {this.formatDate(event.scheduledStart)}
                </div>
                <div class='mb-1'>
                    <i class='fa-regular fa-clock me-2' style={{ width: '14px' }} />
                    {this.formatTime(event.scheduledStart)} - {this.formatTime(event.scheduledEnd)}
                    <span class='ms-1 text-muted'>({this.formatDurationFromDates(event.scheduledStart, event.scheduledEnd)})</span>
                </div>
                {event.eventType && (
                    <div class='mb-1'>
                        <i class='fa-solid fa-tag me-2' style={{ width: '14px' }} />
                        <span class='badge' style={{ backgroundColor: eventTypeColors[event.eventType] || '#6c757d' }}>
                            {event.eventType}
                        </span>
                    </div>
                )}
                {event.description && (
                    <div class='mt-2 text-muted' style={{ fontSize: '0.8rem' }}>
                        {event.description}
                    </div>
                )}
                <div class='mt-2 d-flex flex-wrap gap-1' style={{ fontSize: '0.75rem' }}>
                    {totalDays > 1 && (
                        <span class='badge bg-info text-dark'>Dag {dayNumber}/{totalDays}</span>
                    )}
                    {isContinuation && (
                        <span class='badge bg-warning text-dark'>Fortsettelse</span>
                    )}
                    {isTruncated && (
                        <span class='badge bg-warning text-dark'>Fortsetter</span>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { event, style, canEdit, darkmode, isTruncated, isContinuation, isFirstDay, isLastDay, isMiddleDay, totalDays, dayNumber, channelColor, isCompact } = this.props;
        const { isDragging, isResizing, showTooltip } = this.state;

        const bgColor = event.backgroundColor || event.color || channelColor || eventTypeColors[event.eventType] || '#6c757d';
        const textColor = this.getContrastColor(bgColor);

        // Adjust border-radius based on segment position for multi-day events
        let borderRadius = '4px';
        if (totalDays > 1) {
            if (isFirstDay) {
                borderRadius = '4px 4px 0 0'; // Rounded top only
            } else if (isLastDay) {
                borderRadius = '0 0 4px 4px'; // Rounded bottom only
            } else if (isMiddleDay) {
                borderRadius = '0'; // No rounded corners
            }
        }

        const blockStyle = {
            ...style,
            backgroundColor: bgColor,
            color: textColor,
            opacity: isDragging ? 0.5 : 1,
            cursor: canEdit ? 'grab' : 'pointer',
            borderRadius,
            padding: '2px 4px',
            overflow: 'hidden',
            fontSize: '0.75rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: isResizing ? 'none' : 'opacity 0.2s',
        };

        // Compact mode rendering (30px narrow column - vertical title with hover tooltip)
        if (isCompact) {
            return (
                <div
                    ref={el => this.blockRef = el}
                    class='event-block'
                    style={{
                        ...blockStyle,
                        padding: '2px 1px',
                    }}
                    onClick={this.handleClick}
                    draggable={canEdit}
                    onDragStart={this.handleDragStart}
                    onDragEnd={this.handleDragEnd}
                    onMouseEnter={this.handleMouseEnter}
                    onMouseLeave={this.handleMouseLeave}
                >
                    {/* Continuation indicator at top */}
                    {(isContinuation || (totalDays > 1 && !isFirstDay)) && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
                                borderTopLeftRadius: isFirstDay ? '4px' : '0',
                                borderTopRightRadius: isFirstDay ? '4px' : '0',
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    {/* Vertical title - reads top to bottom */}
                    <div style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        paddingTop: (isContinuation || (totalDays > 1 && !isFirstDay)) ? '6px' : '2px',
                        paddingBottom: (isTruncated || (totalDays > 1 && !isLastDay)) ? '6px' : '2px',
                    }}>
                        {event.timingType === 'hard' && (
                            <i class='fa-solid fa-lock' style={{ fontSize: '0.5rem', marginBottom: '2px' }} />
                        )}
                        {event.title || 'Untitled'}
                        <span style={{
                            fontSize: '0.55rem',
                            marginTop: '6px',
                            fontWeight: 'normal',
                            backgroundColor: 'rgba(0,0,0,0.25)',
                            padding: '3px 4px',
                            borderRadius: '3px',
                        }}>
                            {this.formatDurationFromDates(event.scheduledStart, event.scheduledEnd)}
                        </span>
                        {totalDays > 1 && (
                            <span style={{ fontSize: '0.5rem', marginTop: '2px', opacity: 0.8 }}>{dayNumber}/{totalDays}</span>
                        )}
                    </div>

                    {/* Truncation indicator */}
                    {(isTruncated || (totalDays > 1 && !isLastDay)) && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 4px)',
                                borderBottomLeftRadius: isLastDay ? '4px' : '0',
                                borderBottomRightRadius: isLastDay ? '4px' : '0',
                                pointerEvents: 'none',
                            }}
                        />
                    )}

                    {/* Resize handle */}
                    {canEdit && (
                        <div
                            class='resize-handle'
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                cursor: 'ns-resize',
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.2))',
                            }}
                            onMouseDown={this.handleResizeStart}
                        />
                    )}

                    {/* Render tooltip via portal */}
                    {showTooltip && createPortal(this.renderTooltip(), document.body)}
                </div>
            );
        }

        // Normal mode rendering
        return (
            <div
                ref={el => this.blockRef = el}
                class='event-block'
                style={blockStyle}
                onClick={this.handleClick}
                draggable={canEdit}
                onDragStart={this.handleDragStart}
                onDragEnd={this.handleDragEnd}
            >
                {/* Continuation indicator at top (show for continuations and non-first days of multi-day events) */}
                {(isContinuation || (totalDays > 1 && !isFirstDay)) && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '8px',
                            background: 'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 6px)',
                            borderTopLeftRadius: isFirstDay ? '4px' : '0',
                            borderTopRightRadius: isFirstDay ? '4px' : '0',
                            pointerEvents: 'none',
                        }}
                        title={totalDays > 1 ? `Dag ${dayNumber} av ${totalDays}` : 'Fortsettelse fra forrige sendedag'}
                    />
                )}

                {/* Day indicator badge for multi-day events */}
                {totalDays > 1 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '2px',
                            right: '2px',
                            padding: '1px 4px',
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '3px',
                            color: '#ffffff',
                            zIndex: 2,
                        }}
                        title={`Dag ${dayNumber} av ${totalDays}`}
                    >
                        {dayNumber}/{totalDays}
                    </div>
                )}

                <div class='d-flex justify-content-between align-items-start' style={(isContinuation || (totalDays > 1 && !isFirstDay)) ? { marginTop: '6px' } : {}}>
                    <strong class='text-truncate' style={totalDays > 1 ? { flex: 1, paddingRight: '28px' } : { flex: 1 }}>
                        {event.title || 'Untitled'}
                    </strong>
                    {event.timingType === 'hard' && (
                        <i class='fa-solid fa-lock ms-1' style='font-size: 0.65rem;' title='Hard timing' />
                    )}
                </div>
                <div class='text-truncate' style='opacity: 0.9; font-size: 0.7rem;'>
                    {event.sourceType && sourceTypeIcons[event.sourceType] && (
                        <i class={`${sourceTypeIcons[event.sourceType]} me-1`} style='font-size: 0.6rem;' title={event.sourceType} />
                    )}
                    {this.formatTime(event.scheduledStart)} - {this.formatTime(event.scheduledEnd)}
                    {event.durationSeconds && (
                        <span class='ms-1'>({this.formatDuration(event.durationSeconds)})</span>
                    )}
                </div>
                {event.subtitle && (
                    <div class='text-truncate' style='opacity: 0.8; font-size: 0.65rem;'>
                        {event.subtitle}
                    </div>
                )}
                {event.description && (
                    <div class='text-truncate' style='opacity: 0.7; font-size: 0.7rem;'>
                        {event.description}
                    </div>
                )}

                {/* Truncation indicator (show for truncated events and non-last days of multi-day events) */}
                {(isTruncated || (totalDays > 1 && !isLastDay)) && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '8px',
                            background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.3) 3px, rgba(255,255,255,0.3) 6px)',
                            borderBottomLeftRadius: isLastDay ? '4px' : '0',
                            borderBottomRightRadius: isLastDay ? '4px' : '0',
                            pointerEvents: 'none',
                        }}
                        title={totalDays > 1 ? `Fortsetter til dag ${dayNumber + 1}` : 'Hendelsen fortsetter etter sendetidspunktets slutt'}
                    />
                )}

                {/* Resize handle */}
                {canEdit && (
                    <div
                        class='resize-handle'
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '6px',
                            cursor: 'ns-resize',
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.2))',
                        }}
                        onMouseDown={this.handleResizeStart}
                    />
                )}
            </div>
        );
    }

    // Helper to determine text color based on background
    getContrastColor(hexColor) {
        // Remove # if present
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    }
}

export default EventBlock;
